import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import { API_BASE_URL } from "../../../config/config";
import { saveAs } from 'file-saver';

const sensorColors = [
  "#e53935",
  "#43a047",
  "#1e88e5",
  "#fb8c00",
  "#8e24aa",
  "#00acc1",
];

const WaterLevelSensorGraph = ({ deviceCode, currentDevice, sensordata }) => {
  const [summaryData, setSummaryData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [seriesNames, setSeriesNames] = useState([]);
  const [sensorVisibility, setSensorVisibility] = useState({});
  const [yAxisTicks, setYAxisTicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  // Add state to store sensor units
  const [sensorUnits, setSensorUnits] = useState({});
  // Add state for current time in minutes
  const [currentTimeMinutes, setCurrentTimeMinutes] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartRef = useRef(null);

  // Get today's date in "DD-MM-YYYY" format
  const today = dayjs().format("DD-MM-YYYY");

  useEffect(() => {
    // Set current time in minutes
    const now = dayjs();
    const currentMinutes = now.hour() * 60 + now.minute();
    setCurrentTimeMinutes(currentMinutes);

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}api/sensor/water-level-data`,
          {
            params: { deviceCode, fromDate: today, toDate: today },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data && response.data.sensors) {
          const sensors = response.data.sensors;
          setSummaryData(sensors);
          mergeChartData(sensors);
        }
      } catch (err) {
        console.error("Error fetching water level summary:", err);
        setError("Failed to fetch sensor data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [deviceCode, today]);

  const mergeChartData = (sensors) => {
    const now = dayjs();
    let currentMinutes = now.hour() * 60 + now.minute();

    setCurrentTimeMinutes(currentMinutes);

    let maxWaterLevel = 0;
    const units = {};

    sensors.forEach((sensor) => {
      if (sensor.presentLevelValues) {
        Object.values(sensor.presentLevelValues).forEach((value) => {
          if (value != null && value > maxWaterLevel) {
            maxWaterLevel = value;
          }
        });
      }
    });

    // Extract units from sensordata prop
    if (sensordata && Array.isArray(sensordata)) {
      sensordata.forEach((sensor) => {
        if (sensor.sensor_name && sensor.unit) {
          units[sensor.sensor_name] = sensor.unit;
        }
      });
    }

    sensors.forEach((sensor) => {
      if (sensor.sensor_name && !units[sensor.sensor_name]) {
        const matchingSensorData = sensordata?.find(s => s.sensor_name === sensor.sensor_name);
        if (matchingSensorData?.unit) {
          units[sensor.sensor_name] = matchingSensorData.unit;
        }
      }
    });

    setSensorUnits(units);

    const motorConstant = maxWaterLevel > 0 ? 0.1 * maxWaterLevel : 10;

    // *** CHANGED: Find ALL motor sensors instead of just one ***
    const motorSensors = sensors.filter(
      (sensor) => sensor.sensor_sub_id === "00" && sensor.motorLogs
    );

    const timeDataMap = new Map();

    // Add water level sensor data points
    sensors.forEach((sensor) => {
      if (sensor.presentLevelValues) {
        Object.entries(sensor.presentLevelValues).forEach(([time, value]) => {
          if (!timeDataMap.has(time)) {
            timeDataMap.set(time, {
              time,
              timeMinutes: convertTimeToMinutes(time)
            });
          }
          timeDataMap.get(time)[sensor.sensor_name] = value;
        });
      }
    });

    // *** CHANGED: Process each motor sensor individually ***
    motorSensors.forEach((motorSensor) => {
      const motorSeriesName = motorSensor.sensor_name; // Use actual motor name

      // Create baseline - motor starts at OFF
      const startOfDayTime = "00:00";
      if (!timeDataMap.has(startOfDayTime)) {
        timeDataMap.set(startOfDayTime, {
          time: startOfDayTime,
          timeMinutes: 0
        });
      }
      timeDataMap.get(startOfDayTime)[motorSeriesName] = 0;

      if (motorSensor.motorLogs && Array.isArray(motorSensor.motorLogs) && motorSensor.motorLogs.length > 0) {

        // Filter valid logs for today
        const validMotorLogs = motorSensor.motorLogs.filter(log => {
          const startMinutes = convertTimeToMinutes(log.startTime);
          if (startMinutes > currentMinutes + 60) {
            return false;
          }
          return true;
        });

        validMotorLogs.sort((a, b) => convertTimeToMinutes(a.startTime) - convertTimeToMinutes(b.startTime));

        // Process each motor log
        validMotorLogs.forEach((log, index) => {
          const startMinutes = convertTimeToMinutes(log.startTime);

          // Add point before start
          const oneMinuteBeforeStart = Math.max(0, startMinutes - 1);
          const beforeStartTime = `${Math.floor(oneMinuteBeforeStart / 60).toString().padStart(2, '0')}:${(oneMinuteBeforeStart % 60).toString().padStart(2, '0')}`;

          if (!timeDataMap.has(beforeStartTime)) {
            timeDataMap.set(beforeStartTime, {
              time: beforeStartTime,
              timeMinutes: oneMinuteBeforeStart
            });
          }
          timeDataMap.get(beforeStartTime)[motorSeriesName] = 0;

          // Add motor start point
          if (!timeDataMap.has(log.startTime)) {
            timeDataMap.set(log.startTime, {
              time: log.startTime,
              timeMinutes: startMinutes
            });
          }
          timeDataMap.get(log.startTime)[motorSeriesName] = motorConstant;

          const isMotorRunning = log.stopTime === "Running";
          const stopMinutes = isMotorRunning ? currentMinutes : convertTimeToMinutes(log.stopTime);

          if (!isMotorRunning) {
            // Add motor stop point
            if (!timeDataMap.has(log.stopTime)) {
              timeDataMap.set(log.stopTime, {
                time: log.stopTime,
                timeMinutes: stopMinutes
              });
            }
            timeDataMap.get(log.stopTime)[motorSeriesName] = 0;

            // Add point after stop
            const oneMinuteAfterStop = Math.min(currentMinutes, stopMinutes + 1);
            const afterStopTime = `${Math.floor(oneMinuteAfterStop / 60).toString().padStart(2, '0')}:${(oneMinuteAfterStop % 60).toString().padStart(2, '0')}`;

            if (!timeDataMap.has(afterStopTime)) {
              timeDataMap.set(afterStopTime, {
                time: afterStopTime,
                timeMinutes: oneMinuteAfterStop
              });
            }
            timeDataMap.get(afterStopTime)[motorSeriesName] = 0;
          } else {
            // Motor still running
            const currentTimeString = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
            if (!timeDataMap.has(currentTimeString)) {
              timeDataMap.set(currentTimeString, {
                time: currentTimeString,
                timeMinutes: currentMinutes
              });
            }
            timeDataMap.get(currentTimeString)[motorSeriesName] = motorConstant;
          }

          // Add intermediate points for long operations
          const endTime = isMotorRunning ? currentMinutes : stopMinutes;
          const timeDiff = endTime - startMinutes;

          if (timeDiff > 10) {
            const numberOfPoints = Math.floor(timeDiff / 15);
            for (let i = 1; i < numberOfPoints; i++) {
              const intermediateMinutes = startMinutes + (timeDiff * i / numberOfPoints);
              const intermediateTimeString = `${Math.floor(intermediateMinutes / 60).toString().padStart(2, '0')}:${Math.floor(intermediateMinutes % 60).toString().padStart(2, '0')}`;

              if (!timeDataMap.has(intermediateTimeString)) {
                timeDataMap.set(intermediateTimeString, {
                  time: intermediateTimeString,
                  timeMinutes: intermediateMinutes
                });
              }
              timeDataMap.get(intermediateTimeString)[motorSeriesName] = motorConstant;
            }
          }
        });

        // Fill gaps with OFF
        timeDataMap.forEach((data) => {
          if (data[motorSeriesName] === undefined) {
            let isMotorOn = false;
            validMotorLogs.forEach((log) => {
              const startMinutes = convertTimeToMinutes(log.startTime);
              const isMotorRunning = log.stopTime === "Running";
              const stopMinutes = isMotorRunning ? currentMinutes : convertTimeToMinutes(log.stopTime);

              if (data.timeMinutes >= startMinutes && data.timeMinutes <= stopMinutes) {
                isMotorOn = true;
              }
            });
            data[motorSeriesName] = isMotorOn ? motorConstant : 0;
          }
        });

        // Add final baseline
        const lastMotorLog = validMotorLogs[validMotorLogs.length - 1];
        if (lastMotorLog && lastMotorLog.stopTime !== "Running") {
          const currentTimeString = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
          if (!timeDataMap.has(currentTimeString)) {
            timeDataMap.set(currentTimeString, {
              time: currentTimeString,
              timeMinutes: currentMinutes
            });
          }
          timeDataMap.get(currentTimeString)[motorSeriesName] = 0;
        }
      } else {
        // No motor logs - OFF all day
        const currentTimeString = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
        if (!timeDataMap.has(currentTimeString)) {
          timeDataMap.set(currentTimeString, {
            time: currentTimeString,
            timeMinutes: currentMinutes
          });
        }
        timeDataMap.get(currentTimeString)[motorSeriesName] = 0;

        timeDataMap.forEach((data) => {
          if (data[motorSeriesName] === undefined) {
            data[motorSeriesName] = 0;
          }
        });
      }
    });

    // Convert to sorted array
    const mergedData = Array.from(timeDataMap.entries())
      .map(([time, values]) => ({
        time,
        ...values
      }))
      .sort((a, b) => a.timeMinutes - b.timeMinutes);

    setChartData(mergedData);

    // *** CHANGED: Create series names including all motors by their actual names ***
    const series = sensors
      .filter((sensor) => sensor.sensor_sub_id !== "00" && sensor.presentLevelValues)
      .map((sensor) => sensor.sensor_name);

    // Add all motor sensors by their names
    motorSensors.forEach((motorSensor) => {
      series.push(motorSensor.sensor_name);
    });

    setSeriesNames(series);
    setSensorVisibility(
      series.reduce((acc, name) => {
        acc[name] = true;
        return acc;
      }, {})
    );

    const computedMax = Math.max(maxWaterLevel, motorConstant);
    let axisMax;
    if (computedMax <= 50) {
      axisMax = 100;
    } else {
      axisMax = Math.ceil((computedMax + 10) / 25) * 25;
    }

    const ticks = [0];
    for (let i = 25; i <= axisMax; i += 25) {
      ticks.push(i);
    }
    setYAxisTicks(ticks);
  };



  // Helper function to convert time string to minutes
  const convertTimeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Generate x-axis ticks based on screen size
  const generateXAxisTicks = () => {
    const ticks = [];
    const interval = isMobile ? 4 : 1; // 4 hours for mobile, 1 hour for desktop

    // Use the current time from state, with proper fallback
    const maxHour = Math.floor((currentTimeMinutes || (dayjs().hour() * 60 + dayjs().minute())) / 60);

    for (let hour = 0; hour <= maxHour; hour += interval) {
      ticks.push(hour * 60); // Convert to minutes
    }

    return ticks;
  };

  // Generate all ticks (major and minor) for mobile
  const generateAllXAxisTicks = () => {
    const ticks = [];
    // Use the current time from state, with proper fallback
    const maxHour = Math.floor((currentTimeMinutes || (dayjs().hour() * 60 + dayjs().minute())) / 60);

    for (let hour = 0; hour <= maxHour; hour++) {
      ticks.push(hour * 60); // every hour in minutes
    }
    return ticks;
  };

  // Format tick label
  const formatXAxisTick = (tickValue) => {
    const hours = Math.floor(tickValue / 60);
    return `${hours.toString().padStart(2, '0')}:00`;
  };

  // Custom tick renderer for XAxis
  const CustomXAxisTick = (props) => {
    const { x, y, payload } = props;
    const hour = Math.floor(payload.value / 60);
    const isMajor = hour % 4 === 0; // Show labels every 4 hours

    if (isMajor) {
      // Major tick with label
      return (
        <g transform={`translate(${x},${y})`}>
          <line x1="0" y1="0" x2="0" y2="6" stroke="#666" strokeWidth="1.5" />
          <text
            x="0"
            y="18"
            textAnchor="middle"
            fill="#666"
            fontSize={10}
          >
            {`${hour.toString().padStart(2, '0')}:00`}
          </text>
        </g>
      );
    } else {
      // Minor tick, no label - show for every hour
      return (
        <g transform={`translate(${x},${y})`}>
          <line x1="0" y1="0" x2="0" y2="3" stroke="#bbb" strokeWidth="0.5" />
        </g>
      );
    }
  };

  const handleToggleSensor = (name) => {
    setSensorVisibility((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleSelectAll = () => {
    setSensorVisibility(
      seriesNames.reduce((acc, name) => {
        acc[name] = true;
        return acc;
      }, {})
    );
  };

  const handleUnselectAll = () => {
    setSensorVisibility(
      seriesNames.reduce((acc, name) => {
        acc[name] = false;
        return acc;
      }, {})
    );
  };

  const allSelected =
    seriesNames.length > 0 && seriesNames.every((name) => sensorVisibility[name]);
  const noneSelected = seriesNames.every((name) => !sensorVisibility[name]);

  const tooltipFormatter = (value, name) => {
    // Check if this is a motor sensor by checking if name is in motorSensors
    const isMotor = summaryData.some(s => s.sensor_sub_id === "00" && s.sensor_name === name);

    if (isMotor) {
      return [value > 0 ? "On" : "Off", name];
    }
    return [value, name];
  };

  const createCanvas = () => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size - larger for better quality
      canvas.width = 1400;
      canvas.height = 900;

      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Set font
      ctx.font = 'bold 28px Arial';
      ctx.fillStyle = '#1976d2';
      ctx.textAlign = 'center';

      // Title
      ctx.fillText('Water Level', canvas.width / 2, 50);

      // Device info
      ctx.font = '18px Arial';
      ctx.fillStyle = '#424242';
      ctx.textAlign = 'left';

      const deviceInfo = [
        `Device: ${currentDevice?.deviceName || 'Unknown Device'}`,
        `ID: ${currentDevice?.deviceId || 'N/A'}`,
        `Location: ${currentDevice?.location || 'N/A'}`,
        `Downloaded: ${dayjs().format('DD-MM-YYYY HH:mm:ss')}`
      ];

      deviceInfo.forEach((info, index) => {
        ctx.fillText(info, 60, 90 + (index * 28));
      });

      // Legends
      ctx.font = 'bold 20px Arial';
      ctx.fillStyle = '#1976d2';
      ctx.textAlign = 'center';
      ctx.fillText('Graph Legends', canvas.width / 2, 240);

      // Draw legend items with units
      const visibleSeries = seriesNames.filter(name => sensorVisibility[name]);
      const legendStartY = 270;
      const legendItemWidth = 280;
      const itemsPerRow = Math.floor(canvas.width / legendItemWidth);

      ctx.font = '16px Arial';
      ctx.fillStyle = '#333';
      ctx.textAlign = 'left';


      visibleSeries.forEach((name, idx) => {
        const color = sensorColors[idx % sensorColors.length];
        const row = Math.floor(idx / itemsPerRow);
        const col = idx % itemsPerRow;
        const x = 60 + (col * legendItemWidth);
        const y = legendStartY + (row * 35);

        // Draw color line
        ctx.fillStyle = color;
        ctx.fillRect(x, y - 6, 30, 5);

        // Draw text with unit
        ctx.fillStyle = '#333';
        let legendText = '';
        if (name === "Motor") {
          legendText = "Motor Status";
        } else {
          const unit = sensorUnits[name] || '';
          legendText = `${name}${unit ? ` (${unit})` : ''}`;
        }
        ctx.fillText(legendText, x + 40, y);
      });

      // Get chart SVG
      const svgElement = chartRef.current?.querySelector('svg');
      if (svgElement) {
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = function () {
          // Draw chart with better positioning
          const chartY = 350;
          const chartHeight = 420;
          const chartWidth = canvas.width - 120;

          ctx.drawImage(img, 60, chartY, chartWidth, chartHeight);

          // Y-axis label - positioned better to avoid clipping
          ctx.save();
          ctx.translate(30, chartY + chartHeight / 2);
          ctx.rotate(-Math.PI / 2);
          ctx.font = 'bold 16px Arial';
          ctx.fillStyle = '#424242';
          ctx.textAlign = 'center';
          ctx.fillText('Water Level', 0, 0);
          ctx.restore();

          // Enhanced footer with technical details
          ctx.font = 'bold 14px Arial';
          ctx.fillStyle = '#1976d2';
          ctx.textAlign = 'center';
          ctx.fillText('Generated from Aquabrim Water Level Monitoring System', canvas.width / 2, 810);

          ctx.font = '12px Arial';
          ctx.fillStyle = '#666';
          ctx.fillText(' Real-time sensor readings captured at 5-minute intervals with timestamp precision', canvas.width / 2, 830);
          ctx.fillText('For technical assistance or queries, please contact our support team', canvas.width / 2, 865);

          URL.revokeObjectURL(svgUrl);
          resolve(canvas);
        };

        img.onerror = function () {
          URL.revokeObjectURL(svgUrl);
          resolve(canvas);
        };

        img.src = svgUrl;
      } else {
        resolve(canvas);
      }
    });
  };

  const downloadAsJPG = async () => {
    if (downloading) return;
    setDownloading(true);

    try {
      const canvas = await createCanvas();

      canvas.toBlob((blob) => {
        const fileName = `water_level_graph_${currentDevice?.deviceName?.replace(/[^a-zA-Z0-9]/g, '_') || 'device'}_${today.replace(/[/\?%*:|"<>]/g, '-')}.jpg`;
        saveAs(blob, fileName);
        setDownloading(false);
      }, 'image/jpeg', 0.95);

    } catch (error) {
      console.error('Error downloading JPG:', error);
      alert('Failed to download chart. Please try again.');
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={600}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  return (
    <Card
      sx={{
        width: "100%",
        overflowX: "hidden",
      }}
    >
      <Box sx={{ width: "100%", padding: { xs: 1, sm: 2 } }}>
        {/* Header with title and download button - mobile optimized */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          gap: 1
        }}>
          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              fontWeight: 600,
              color: '#1976d2',
              flexGrow: 1
            }}
          >
            Water Level
          </Typography>

          <Tooltip title="Download Graph as JPG">
            <IconButton
              onClick={downloadAsJPG}
              disabled={downloading}
              sx={{
                backgroundColor: '#1976d2',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1565c0'
                },
                '&:disabled': {
                  backgroundColor: '#ccc'
                },
                width: { xs: 36, sm: 48 },
                height: { xs: 36, sm: 48 }
              }}
            >
              {downloading ? <CircularProgress size={isMobile ? 18 : 24} color="inherit" /> : <DownloadIcon fontSize={isMobile ? "small" : "medium"} />}
            </IconButton>
          </Tooltip>
        </Box>
        {/* Add padding top on mobile to accommodate absolute positioned button */}
        <Box sx={{ paddingTop: 0 }}>
          {/* Render checkboxes for each series - mobile optimized */}
          <Box sx={{ marginBottom: 2 }}>
            {seriesNames.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAll}
                  disabled={allSelected}
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    py: 0.25,
                    px: 1.25,
                    borderColor: '#1976d2',
                    color: '#1976d2',
                  }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleUnselectAll}
                  disabled={noneSelected}
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    py: 0.25,
                    px: 1.25,
                    borderColor: '#c62828',
                    color: '#c62828',
                  }}
                >
                  Unselect All
                </Button>
              </Box>
            )}
            <Grid
              container
              spacing={{ xs: 0.5, sm: 1, md: 2 }}
              justifyContent="flex-start"
              wrap="wrap"
            >
              {seriesNames.map((name, idx) => (
                <Grid key={name} item xs="auto" sm="auto" md="auto" lg="auto">
                  <FormControlLabel
                    label={name}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.8rem",
                          md: "0.9rem",
                        },
                        whiteSpace: 'nowrap'
                      },
                      margin: { xs: '2px', sm: '4px' },
                      minWidth: 'auto'
                    }}
                    control={
                      <Checkbox
                        checked={sensorVisibility[name] || false}
                        onChange={() => handleToggleSensor(name)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          color: sensorColors[idx % sensorColors.length],
                          "&.Mui-checked": {
                            color: sensorColors[idx % sensorColors.length],
                          },
                          padding: { xs: '4px', sm: '9px' }
                        }}
                      />
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box
            ref={chartRef}
            sx={{
              width: "100%",
              height: { xs: 250, sm: 350, md: 400 },
              overflow: "hidden",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{
                  top: 10,
                  right: isMobile ? 10 : 30,
                  left: isMobile ? -20 : -30,
                  bottom: 10
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="timeMinutes"
                  type="number"
                  domain={[0, Math.max(currentTimeMinutes || (dayjs().hour() * 60 + dayjs().minute()), 60)]} // Ensure minimum of 1 hour
                  ticks={isMobile ? generateAllXAxisTicks() : generateXAxisTicks()}
                  tick={isMobile ? <CustomXAxisTick /> : { fontSize: 12 }}
                  tickFormatter={!isMobile ? formatXAxisTick : undefined}
                  interval={0}
                  label={{
                    value: "Time (HH:mm)",
                    position: "insideBottom",
                    offset: -5,
                    fontSize: isMobile ? 10 : 12,
                  }}
                />
                <YAxis
                  ticks={yAxisTicks}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                />
                <RechartsTooltip
                  formatter={tooltipFormatter}
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #ccc",
                    fontSize: isMobile ? '12px' : '14px'
                  }}
                  labelFormatter={(value) => {
                    if (typeof value === 'number') {
                      const hours = Math.floor(value / 60);
                      const minutes = value % 60;
                      return `Time: ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                    }
                    return `Time: ${value}`;
                  }}
                />
                {seriesNames.map((name, idx) => {
                  if (!sensorVisibility[name]) return null;

                  // Check if this series is a motor
                  const isMotor = summaryData.some(s => s.sensor_sub_id === "00" && s.sensor_name === name);

                  return (
                    <Line
                      key={name}
                      type={isMotor ? "stepAfter" : "monotone"}
                      dataKey={name}
                      stroke={sensorColors[idx % sensorColors.length]}
                      strokeWidth={isMobile ? 1.5 : 2}
                      dot={false}
                      connectNulls={!isMotor}
                    />
                  );
                })}

              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default WaterLevelSensorGraph;
